import React, { Component } from 'react';

import PaginationComponent from './PaginationComponent';
import TableStateCard from './TableStateCard';
import { sortClass, nextSortDir, sortAriaSort } from '../utils/table';
import { navigateTo } from '../utils/navigation';
import { scheduleDebouncedCallback, clearDebouncedCallback } from '../utils/pagination';
import { hasActiveSearch, getSearchEmptyMessage } from '../utils/tableState';
import {
    getCollections,
    deleteCollection,
    normalizeCollection,
    placementLabel,
    isPlaced,
    isSystemSlug,
    isConflict,
} from '../utils/collectionsApi';

// Listado de colecciones de la home.
//
// Antes esta tabla mostraba id y nombre y nada más: para saber si "Novedades" tenía 0 o 40
// productos había que entrar a la pantalla de asignación y contar a ojo, y no había forma de ver
// si una colección estaba publicada. Ahora cada fila dice de un vistazo lo que el dueño necesita
// para decidir: si se ve en la web, dónde, en qué orden y con cuántos productos — separando los
// ASIGNADOS de los que REALMENTE se ven (un producto puede estar en la colección y no mostrarse,
// por ejemplo si está despublicado o sin stock).
class TableLabelsComponent extends Component {

    constructor() {
        super();
        this.state = {
            quantityShowRows: 25,
            currentPage: 1,
            totalItems: 0,
            search: '',
            searchInput: '',
            sortBy: 'name',
            sortDir: 'asc',
            data: [],
            loaded: false,
            loadError: null,
            deletingId: null,
        };
        this.mounted = false;
        this.fetchSeq = 0;
    }

    componentDidMount() {
        this.mounted = true;
        this.fetchData();
    }

    componentWillUnmount() {
        this.mounted = false;
        clearDebouncedCallback(this, 'searchDebounceTimeout');
    }

    fetchData = ({
        page = this.state.currentPage,
        limit = this.state.quantityShowRows,
        search = this.state.search,
        sortBy = this.state.sortBy,
        sortDir = this.state.sortDir,
        showLoader = true,
    } = {}) => {
        const seq = ++this.fetchSeq;
        if (showLoader) {
            this.setState({ loaded: false, loadError: null });
        }

        getCollections({ page, limit, search, sortBy, sortDir })
            .then((paginated) => {
                if (!this.mounted || seq !== this.fetchSeq) return;
                this.setState({
                    data: (paginated.items || []).map(normalizeCollection),
                    quantityShowRows: paginated.limit || Number(limit),
                    currentPage: paginated.page || Number(page),
                    totalItems: paginated.total || 0,
                    search,
                    sortBy,
                    sortDir,
                    loaded: true,
                    loadError: null,
                });
            })
            .catch((error) => {
                // Sin catch, un rechazo de red dejaba la tabla pegada en el loader para siempre.
                if (!this.mounted || seq !== this.fetchSeq) return;
                this.setState({ loaded: true, loadError: error.message });
            });
    }

    editCollection = (id) => {
        navigateTo(`/editLabel?id=${id}`);
    }

    curateCollection = (id) => {
        navigateTo(`/labelAssign?id=${id}`);
    }

    removeCollection = (item) => {
        if (isSystemSlug(item.slug)) {
            // Se explica antes de gastar el viaje al servidor. El backend igual responde 409:
            // esto es la explicación, no el control.
            alert(`"${item.name}" es una colección de sistema: la home la pide por su nombre corto `
                + `(${item.slug}), así que no se puede borrar. Si no la querés mostrar, entrá a editarla y despublicala.`);
            return;
        }

        const cuantos = item.productCount != null ? `${item.productCount} producto(s) asignado(s)` : 'sus asignaciones';
        if (!window.confirm(`Se va a eliminar la colección "${item.name}" y ${cuantos}.\nLos productos NO se borran.\n\n¿Confirmás?`)) {
            return;
        }

        this.setState({ deletingId: item.id });
        deleteCollection(item.id)
            .then(() => {
                if (!this.mounted) return;
                this.setState({ deletingId: null });
                // Si era la última fila de la página, quedarse ahí mostraría una grilla vacía con
                // el paginador diciendo que hay resultados y sin forma de volver.
                const ultimaDeLaPagina = this.state.data.length === 1 && this.state.currentPage > 1;
                this.fetchData({ showLoader: false, page: ultimaDeLaPagina ? this.state.currentPage - 1 : undefined });
            })
            .catch((error) => {
                if (!this.mounted) return;
                this.setState({ deletingId: null });
                alert(isConflict(error)
                    ? `${error.message}\n\nEs una colección de sistema: la home la pide por su nombre corto, `
                        + 'así que borrarla dejaría una sección rota. Si no la querés mostrar, despublicala.'
                    : error.message);
            });
    }

    renderProductsCell = (item) => {
        if (item.productCount == null) {
            return <span style={{ color: '#818ea3' }}>—</span>;
        }

        const ocultos = item.visibleCount != null ? Math.max(0, item.productCount - item.visibleCount) : 0;
        return (
            <span title={ocultos > 0
                ? `${ocultos} producto(s) asignado(s) no se ven en la web. Entrá a "Curar" para ver el motivo de cada uno.`
                : 'Asignados a la colección / visibles realmente en la web'}>
                <strong>{item.productCount}</strong>
                <span style={{ color: '#818ea3' }}> asignados</span>
                {item.visibleCount != null ? (
                    <>
                        <span style={{ color: '#818ea3' }}> · </span>
                        <span className={ocultos > 0 ? 'text-warning' : ''}>{item.visibleCount}</span>
                        <span style={{ color: '#818ea3' }}> visibles</span>
                    </>
                ) : null}
            </span>
        );
    }

    drawRow = (item, index) => {
        const publicadaSinLugar = item.isPublic && !isPlaced(item.placement);
        // Dos situaciones que antes se pintaban igual, las dos en amarillo:
        //   · SIN CURAR (0 asignados): es el estado normal de una colección recién creada y el de
        //     "Novedades", que por decisión del dueño nace publicada y vacía para curarla a mano.
        //     La tienda no dibuja la sección mientras no tenga productos (Index.tsx gatea con
        //     `newArrivals.length > 0`), así que no queda ningún hueco a la vista. Pintar el
        //     estado esperado como advertencia empuja a "arreglar" algo que está bien.
        //   · CURADA PERO INVISIBLE (hay asignados y ninguno se ve): eso sí es un problema
        //     —se curaron productos ocultos o archivados— y se sigue avisando en amarillo.
        const sinCurar = item.isPublic && item.productCount === 0;
        const publicadaVacia = item.isPublic && item.productCount > 0 && item.visibleCount === 0;
        const borrando = this.state.deletingId === item.id;

        return (
            <tr role="row" className="odd" key={item.id} style={{ background: index % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                <td tabIndex="0" className="sorting_1">
                    {item.name}
                    {item.slug ? <div><code style={{ fontSize: '11px' }}>{item.slug}</code></div> : null}
                </td>
                <td style={{ textAlign: 'center' }}>
                    <span className={`badge ${item.isPublic ? 'badge-success' : 'badge-secondary'}`}>
                        {item.isPublic ? 'Publicada' : 'Privada'}
                    </span>
                    {publicadaSinLugar ? (
                        <div className="text-warning" style={{ fontSize: '11px' }} title="Publicada pero sin ubicación: no se muestra en ningún lado.">
                            sin ubicación
                        </div>
                    ) : null}
                    {sinCurar ? (
                        <div style={{ fontSize: '11px', color: '#818ea3' }} title="Todavía no tiene productos. La tienda no muestra la sección hasta que le agregues alguno.">
                            todavía sin productos
                        </div>
                    ) : null}
                    {publicadaVacia ? (
                        <div className="text-warning" style={{ fontSize: '11px' }} title="Tiene productos asignados pero ninguno se ve en la web (están ocultos o archivados). Entrá a 'Curar' para ver el motivo de cada uno.">
                            sin productos visibles
                        </div>
                    ) : null}
                </td>
                <td>{placementLabel(item.placement)}</td>
                <td style={{ textAlign: 'center' }}>{item.sortOrder}</td>
                <td>{this.renderProductsCell(item)}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                        id={`button-edit-${item.id}`}
                        type="button"
                        className="btn btn-sm btn-white"
                        style={{ fontSize: '12px' }}
                        onClick={() => this.editCollection(item.id)}
                    >Editar</button>
                    <button
                        id={`button-assign-${item.id}`}
                        type="button"
                        className="btn btn-sm btn-white ml-1"
                        style={{ fontSize: '12px' }}
                        onClick={() => this.curateCollection(item.id)}
                    >Curar</button>
                    <button
                        id={`button-delete-${item.id}`}
                        type="button"
                        className="btn btn-sm btn-white text-danger ml-1"
                        style={{ fontSize: '12px' }}
                        disabled={borrando}
                        onClick={() => this.removeCollection(item)}
                    >{borrando ? 'Eliminando…' : 'Eliminar'}</button>
                </td>
            </tr>
        );
    }

    loadData = () => {
        if (!this.state.loaded) {
            return <tbody></tbody>;
        }

        if (this.state.loadError) {
            return (
                <tbody>
                    <tr><td colSpan="6" style={{ textAlign: 'center', padding: '10px', border: '1px solid rgb(225, 229, 235)' }}>
                        <span className="text-danger">{this.state.loadError}</span>
                        <button type="button" className="btn btn-sm btn-white ml-2" onClick={() => this.fetchData()}>Reintentar</button>
                    </td></tr>
                </tbody>
            );
        }

        const items = this.state.data.map((item, index) => this.drawRow(item, index));
        if (items.length === 0) {
            items.push(
                <tr key="empty"><td colSpan="6" style={{ textAlign: 'center', padding: '10px', border: '1px solid rgb(225, 229, 235)' }}>
                    {getSearchEmptyMessage(this.state.searchInput || this.state.search)}
                </td></tr>
            );
        }
        return <tbody>{items}</tbody>;
    }

    onChangeShowRows = () => {
        const itemSelected = Number(document.getElementById('select_rows').value);
        this.fetchData({ page: 1, limit: itemSelected });
    }

    onChangeInput = (search) => {
        this.setState({ searchInput: search });
        scheduleDebouncedCallback(this, 'searchDebounceTimeout', () => {
            this.fetchData({ page: 1, search, showLoader: false });
        });
    }

    orderData = (column) => {
        this.fetchData({
            page: 1,
            sortBy: column,
            sortDir: nextSortDir(column, this.state.sortBy, this.state.sortDir),
        });
    }

    // Props de una cabecera ordenable: la clase y el aria-sort se derivan del state, así que
    // sobreviven al remonte que provoca el loader.
    sortableHeader = (column) => ({
        onClick: () => this.orderData(column),
        className: sortClass(column, this.state.sortBy, this.state.sortDir),
        'aria-sort': sortAriaSort(column, this.state.sortBy, this.state.sortDir),
        tabIndex: '0',
    })

    render() {
        const searchActive = hasActiveSearch(this.state.searchInput, this.state.search);

        if (!this.state.loaded) {
            return <TableStateCard loading />;
        }

        if (this.state.loadError && this.state.data.length === 0) {
            return (
                <TableStateCard
                    icon="error_outline"
                    title="No pudimos cargar las colecciones"
                    message={this.state.loadError}
                />
            );
        }

        if (this.state.totalItems === 0 && !searchActive) {
            return (
                <TableStateCard
                    icon="sell"
                    title="Todavía no hay colecciones"
                    message='Las colecciones son las secciones curadas de la home: el carrusel de arriba y los recuadros de categoría. Creá la primera con el botón "+" y después elegí qué productos van adentro.'
                />
            );
        }

        return (
            <div id="DataTables_Table_0_wrapper" className="dataTables_wrapper no-footer">
                <div className="dataTables_length" id="DataTables_Table_0_length">
                    <label>Show
                    <select id="select_rows" name="DataTables_Table_0_length" aria-controls="DataTables_Table_0" value={this.state.quantityShowRows} onChange={() => this.onChangeShowRows()}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    entries</label>
                </div>
                <div id="DataTables_Table_0_filter" className="dataTables_filter">
                    <label>Search:
                    <input id="input_search" type="search" className="" placeholder="" aria-controls="DataTables_Table_0" value={this.state.searchInput} onChange={(e) => this.onChangeInput(e.target.value)}/>
                    </label>
                </div>

                <table className="transaction-history d-none dataTable no-footer dtr-inline" id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info">
                    <thead>
                        <tr role="row">
                            {/* Ordenable sólo por lo que el backend sabe ordenar (SORTABLE_LABEL_COLUMNS:
                                id, name, slug, placement, sort_order). "Publicada" y "Productos" no
                                están: una flecha que no ordena nada es peor que no tener flecha. */}
                            <th {...this.sortableHeader('name')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '26%' }}>Colección</th>
                            <th rowSpan="1" colSpan="1" style={{ width: '13%', textAlign: 'center' }}>Publicada</th>
                            <th {...this.sortableHeader('placement')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '16%' }}>Ubicación en la home</th>
                            <th {...this.sortableHeader('sort_order')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '8%', textAlign: 'center' }}>Orden</th>
                            <th rowSpan="1" colSpan="1" style={{ width: '19%' }}>Productos</th>
                            <th rowSpan="1" colSpan="1" style={{ width: '18%', textAlign: 'right' }}>Acciones</th>
                        </tr>
                    </thead>
                    {this.loadData()}
                </table>

                <PaginationComponent
                    totalItems={this.state.totalItems}
                    quantityRows={this.state.quantityShowRows}
                    currentPage={this.state.currentPage}
                    onPageChange={(page) => this.fetchData({ page })}
                />
            </div>
        );
    }
}

export default TableLabelsComponent;
